/**
 * Canonical error keys used as `issueKey` in AppTracer reports.
 *
 * Naming convention: <layer>-<transport/method>-<code>
 *   - layer:     "http" (axios), "boundary" (React error boundary), "plugin" (SDK plugin)
 *   - transport: "network" | "get" | "post" | "put" | "patch" | "delete"
 *   - code:      HTTP status code or descriptive slug
 *
 * Add new entries here and reference them by enum value everywhere in the app.
 * Keep entries sorted alphabetically within each group.
 * See ERRORS.md for the full human-readable catalogue.
 */
export const enum TracerIssueKey {
  // ── React error boundaries ──────────────────────────────────────────────────
  BoundaryRoot = "error-boundary-root",

  // ── AppTracer / SDK internals ───────────────────────────────────────────────
  PluginUnavailable = "plugin-unavailable",

  // ── HTTP – network-level (no response received) ─────────────────────────────
  HttpNetworkDelete = "http-network-DELETE",
  HttpNetworkGet = "http-network-GET",
  HttpNetworkPatch = "http-network-PATCH",
  HttpNetworkPost = "http-network-POST",
  HttpNetworkPut = "http-network-PUT",

  // ── HTTP – server errors (5xx) ───────────────────────────────────────────────
  HttpServerDeleteError = "http-500-DELETE",
  HttpServerGetError = "http-500-GET",
  HttpServerPatchError = "http-500-PATCH",
  HttpServerPostError = "http-500-POST",
  HttpServerPutError = "http-500-PUT",

  // ── HTTP – client errors (4xx) reported explicitly ──────────────────────────
  HttpUnauthorized = "http-401",
  HttpForbidden = "http-403",
  HttpNotFound = "http-404",
}

/**
 * Human-readable titles for each issue key (English).
 * Used in the `FullPageError` fallback UI and in tester notifications.
 */
export const TRACER_ERROR_TITLES: Record<string, string> = {
  [TracerIssueKey.BoundaryRoot]:
    "Unhandled render error – the application crashed and was caught by the root Error Boundary",
  [TracerIssueKey.PluginUnavailable]:
    "AppTracer plugin is unavailable – the monitoring SDK failed to initialise",

  [TracerIssueKey.HttpNetworkGet]:
    "Network error on GET request – no response received (offline or unreachable server)",
  [TracerIssueKey.HttpNetworkPost]:
    "Network error on POST request – no response received (offline or unreachable server)",
  [TracerIssueKey.HttpNetworkPut]:
    "Network error on PUT request – no response received (offline or unreachable server)",
  [TracerIssueKey.HttpNetworkPatch]:
    "Network error on PATCH request – no response received (offline or unreachable server)",
  [TracerIssueKey.HttpNetworkDelete]:
    "Network error on DELETE request – no response received (offline or unreachable server)",

  [TracerIssueKey.HttpServerGetError]:
    "Server error (5xx) on GET request – internal server error",
  [TracerIssueKey.HttpServerPostError]:
    "Server error (5xx) on POST request – internal server error",
  [TracerIssueKey.HttpServerPutError]:
    "Server error (5xx) on PUT request – internal server error",
  [TracerIssueKey.HttpServerPatchError]:
    "Server error (5xx) on PATCH request – internal server error",
  [TracerIssueKey.HttpServerDeleteError]:
    "Server error (5xx) on DELETE request – internal server error",

  [TracerIssueKey.HttpUnauthorized]:
    "Unauthorised (401) – the session token is missing or expired",
  [TracerIssueKey.HttpForbidden]:
    "Forbidden (403) – the current user does not have permission for this action",
  [TracerIssueKey.HttpNotFound]:
    "Not found (404) – the requested resource does not exist",
};

/**
 * User-facing notification messages shown in the UI (Russian, for end users).
 */
export const TRACER_ERROR_USER_MESSAGES: Record<string, { title: string; description: string }> = {
  [TracerIssueKey.HttpNetworkGet]: {
    title: "Нет соединения с сервером",
    description: "Проверьте подключение к интернету и попробуйте снова.",
  },
  [TracerIssueKey.HttpNetworkPost]: {
    title: "Нет соединения с сервером",
    description: "Данные не были отправлены. Проверьте подключение к интернету.",
  },
  [TracerIssueKey.HttpNetworkPut]: {
    title: "Нет соединения с сервером",
    description: "Данные не были обновлены. Проверьте подключение к интернету.",
  },
  [TracerIssueKey.HttpNetworkPatch]: {
    title: "Нет соединения с сервером",
    description: "Данные не были обновлены. Проверьте подключение к интернету.",
  },
  [TracerIssueKey.HttpNetworkDelete]: {
    title: "Нет соединения с сервером",
    description: "Операция не выполнена. Проверьте подключение к интернету.",
  },
  [TracerIssueKey.HttpServerPostError]: {
    title: "Ошибка сервера",
    description: "При отправке данных произошла внутренняя ошибка сервера. Попробуйте позже.",
  },
  [TracerIssueKey.HttpServerGetError]: {
    title: "Ошибка сервера",
    description: "Не удалось загрузить данные из-за внутренней ошибки сервера.",
  },
  [TracerIssueKey.HttpServerPutError]: {
    title: "Ошибка сервера",
    description: "При сохранении данных произошла внутренняя ошибка сервера.",
  },
  [TracerIssueKey.HttpServerPatchError]: {
    title: "Ошибка сервера",
    description: "При обновлении данных произошла внутренняя ошибка сервера.",
  },
  [TracerIssueKey.HttpServerDeleteError]: {
    title: "Ошибка сервера",
    description: "При удалении произошла внутренняя ошибка сервера.",
  },
  [TracerIssueKey.BoundaryRoot]: {
    title: "Произошла критическая ошибка",
    description: "Приложение завершило работу неожиданно. Пожалуйста, перезагрузите страницу.",
  },
};
